import { ApiResponse, ApiService } from '../utils/ApiService';
import { CustomXmlService } from './CustomXmlService';
import { ERROR_FILL, JournalListService, ListOwner, ListRefresh } from './JournalListService';
import { JournalTemplate, JournalLine, JournalColumnDefinition, ResolvedJournalSheet, ValidationError, JournalJob, JournalManifest, ListValueNote } from '../types/journal';
import { codeOfListItem, columnLetter, listKeyOf, matchFormOf } from '../utils/journalLists';
import { accountListOf, accountPairsOf } from '../utils/journalAccountTypes';
import { normalizeDescription } from '../utils/journalSheetState';

const EXCEL_EPOCH_OFFSET = 25569; // Days between 1900-01-01 and 1970-01-01
const MS_PER_DAY = 86400000;
const MAX_SHEET_NAME_LENGTH = 31;
const ILLEGAL_SHEET_NAME_CHARS = /[:\\/?*[\]]/g;
const DATA_ROWS = 500;
/** Header row on sheets written before the balance band existed. */
const LEGACY_HEADER_ROW = 1;
/** Balance band in row 1, blank spacer in row 2, headers in row 3. */
const BANDED_HEADER_ROW = 3;
const BALANCE_OK_FILL = '#DFF6DD';
const BALANCE_OFF_FILL = '#FFC7CE';
const BALANCE_OFF_FONT = '#9C0006';
const HEADER_FILL = '#4472C4';
const WARNING_FILL = '#FFEB9C';
/** The journal's description sits in the band's spacer row: label in A2, the value next to it. */
const DESCRIPTION_LABEL = 'Journal description';
const DESCRIPTION_ROW_INDEX = 1;

/** A sheet just inserted, with why its dropdowns could not be added when they could not. */
export interface InsertedJournalSheet extends ResolvedJournalSheet {
  listsError?: string;
}

function excelSerialToISODate(serial: number): string {
  const utcMs = (serial - EXCEL_EPOCH_OFFSET) * MS_PER_DAY;
  const d = new Date(utcMs);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isDateString(val: string): boolean {
  return /^\d{4}-\d{2}-\d{2}/.test(val) || /^\d{2}[./]\d{2}[./]\d{4}/.test(val);
}

function normalizeDateString(val: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
  const match = val.match(/^(\d{2})[./](\d{2})[./](\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return val;
}

/**
 * `listItems` are the column's dropdown items: a cell holding exactly one of them is a pick the
 * add-in was not running to reduce, and is read as its code. Any other text goes as typed, for the
 * check to report.
 */
function readCellValue(val: string | number | boolean | undefined, col: JournalColumnDefinition, listItems?: Set<string>): string | number | null {
  if (val === undefined || val === '' || val === null) return null;

  if (typeof val === 'string' && listItems?.has(val.trim())) return codeOfListItem(val);

  if (col.type === 'date') {
    if (typeof val === 'number') return excelSerialToISODate(val);
    if (typeof val === 'string' && isDateString(val)) return normalizeDateString(val);
    return String(val);
  }

  if (col.type === 'number' || col.type === 'decimal') {
    if (typeof val === 'number') return val;
    const n = Number(val);
    return isNaN(n) ? null : n;
  }

  return typeof val === 'boolean' ? String(val) : (val as string | number);
}

/** Where a manifest's worksheet currently lives, and how full it is. */
type SheetLocation = Omit<ResolvedJournalSheet, 'manifest'>;

/**
 * A journal request the backend refused, with its code so the pane can tell refusals apart, and the
 * lines a refused send failed on.
 */
export class JournalApiError extends Error {
  constructor(message: string, readonly code?: string, readonly status?: number, readonly validationErrors?: ValidationError[]) {
    super(message);
    this.name = 'JournalApiError';
    // The bundle is compiled down to ES5, where a subclass of Error otherwise loses its prototype.
    Object.setPrototypeOf(this, JournalApiError.prototype);
  }
}

/** The backend's refusal of a journal from a template the Bifrost Portal no longer offers this company. */
export const TEMPLATE_INACTIVE_CODE = 'JOURNAL_TEMPLATE_INACTIVE';

/** The backend's refusal of a journal on an ERP connection this company no longer has active. */
export const CONNECTION_INACTIVE_CODE = 'JOURNAL_CONNECTION_INACTIVE';

function refused(response: ApiResponse<unknown>, fallback: string): JournalApiError {
  return new JournalApiError(response.error ?? fallback, response.code, response.status, response.validationErrors);
}

export interface JournalValidationResult {
  valid: boolean;
  errors: ValidationError[];
  jobId: string;
  /** True when the ERP checked the lines too; absent where the backend cannot ask it. */
  erpChecked?: boolean;
}

/** A draft the ERP approves and posts itself, as a Dynamics 365 journal name with approval workflow. */
export function awaitsApprovalInErp(job: JournalJob): boolean {
  if (job.erpResponse?.approvalInErp === true) return true;
  // A backend that predates the flag says so only in the warning.
  return (job.erpResponse?.warnings ?? []).some(w => /needs approval/i.test(w));
}

export type HeaderFit = 'same' | 'appended' | 'different';

/** Columns read past the template's, so a column the template has since dropped is noticed. */
const HEADER_SLACK = 10;

export function compareHeader(row: string[], labels: string[]): HeaderFit {
  const cells = row.map(v => v.trim());
  if (cells.slice(labels.length).some(v => v !== '')) return 'different';
  const present = cells.slice(0, labels.length);
  const end = present.findIndex(v => v === '');
  const filled = end < 0 ? present : present.slice(0, end);
  if (filled.length === 0 || present.slice(filled.length).some(v => v !== '')) return 'different';
  if (filled.some((v, i) => v !== labels[i])) return 'different';
  return filled.length === labels.length ? 'same' : 'appended';
}

export { columnLetter };

/**
 * Position of the debit and credit columns, which are what the balance band
 * sums. Templates without the split (a single signed amount) get no band.
 */
function findAmountPair(columns: JournalColumnDefinition[]): AmountColumns | null {
  const debit = columns.findIndex(c => c.linkedField === 'debit');
  const credit = columns.findIndex(c => c.linkedField === 'credit');
  const balancing = columns.findIndex(c => c.linkedField === 'balancingAccount');
  if (debit < 0 || credit < 0) return null;
  return balancing >= 0 ? { debit, credit, balancing } : { debit, credit };
}

/** Column positions the band reads; `balancing` where lines can carry a balancing account. */
interface AmountColumns { debit: number; credit: number; balancing?: number }

/**
 * Live totals for the sheet, as native Excel formulas so they update while the
 * user types rather than only when the backend validates.
 */
function balanceBandFormulas(pair: AmountColumns, headerRow: number): string[] {
  const firstDataRow = headerRow + 1;
  const lastDataRow = headerRow + DATA_ROWS;
  const range = (index: number) => `${columnLetter(index)}${firstDataRow}:${columnLetter(index)}${lastDataRow}`;

  // A line with a balancing account posts its counter-entry too: its debit is a credit on the balancing
  // account, and its credit a debit, so the totals are what the ERP posts and balance by themselves.
  const total = (own: number, other: number) => pair.balancing === undefined
    ? `=SUM(${range(own)})`
    : `=SUM(${range(own)})+SUMIFS(${range(other)},${range(pair.balancing)},"<>")`;

  return [
    'Total debit', total(pair.debit, pair.credit),
    'Total credit', total(pair.credit, pair.debit),
    'Difference', `=${columnLetter(1)}1-${columnLetter(3)}1`,
    'Lines', `=COUNTA(${range(0)})`,
  ];
}

/** The narrowest and widest a new sheet's column starts, in points; any width after that is the user's. */
const MIN_COLUMN_WIDTH = 120;
const MIN_DATE_COLUMN_WIDTH = 110;
const MAX_COLUMN_WIDTH = 260;
/** A bold header character, and the cell's padding and dropdown arrow beside the text, in points. */
const HEADER_CHAR_WIDTH = 7.5;
const HEADER_PADDING = 24;

/** A new sheet's column width: wide enough to read its whole header, within bounds. */
export function headerColumnWidth(column: Pick<JournalColumnDefinition, 'label' | 'type'>): number {
  const fit = Math.ceil(column.label.trim().length * HEADER_CHAR_WIDTH + HEADER_PADDING);
  const min = column.type === 'date' ? MIN_DATE_COLUMN_WIDTH : MIN_COLUMN_WIDTH;
  return Math.min(MAX_COLUMN_WIDTH, Math.max(min, fit));
}

/** Returns the original manifest unchanged when the worksheet hasn't moved. */
function healManifest(manifest: JournalManifest, location: SheetLocation): JournalManifest {
  if (manifest.worksheetId === location.worksheetId && manifest.worksheetName === location.worksheetName) {
    return manifest;
  }
  return { ...manifest, worksheetId: location.worksheetId, worksheetName: location.worksheetName };
}

/**
 * Build a sheet name that Excel will accept and that no existing sheet is
 * using. Collisions get an Excel-style " (2)", " (3)" suffix rather than
 * letting worksheets.add() throw — a template whose name truncates into
 * another's, or a sheet the user created by hand, must not break Insert.
 */
export function uniqueSheetName(base: string, takenNames: Iterable<string>): string {
  const taken = new Set(Array.from(takenNames, n => n.toLowerCase()));
  const cleaned = base.replace(ILLEGAL_SHEET_NAME_CHARS, '-').trim() || 'Journal';

  const first = cleaned.substring(0, MAX_SHEET_NAME_LENGTH).trim();
  if (!taken.has(first.toLowerCase())) return first;

  for (let n = 2; n < 1000; n++) {
    const suffix = ` (${n})`;
    const stem = cleaned.substring(0, MAX_SHEET_NAME_LENGTH - suffix.length).trim();
    const candidate = `${stem}${suffix}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  throw new Error('Could not find an unused journal sheet name');
}

export class JournalService {
  private apiService: ApiService;
  private xmlService: CustomXmlService;
  private authHeaders: Record<string, string> = {};
  /** Whether each journal's ERP keeps a sent journal as a draft; fixed for a journal, as its connection is. */
  private readonly instanceDraftStates = new Map<string, boolean>();
  readonly lists: JournalListService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
    this.xmlService = new CustomXmlService();
    this.lists = new JournalListService(apiService);
  }

  setAuthHeaders(headers: Record<string, string>): void {
    this.authHeaders = headers;
    this.lists.setAuthHeaders(headers);
  }

  /** The active company and the signed-in user, whose dropdown lists the sheets get. */
  setListOwner(companyId: string, userId: string): void {
    this.lists.setOwner(companyId, userId);
  }

  /**
   * The company's first active ERP connection, read with the action's own headers. For a backend whose
   * Journal rights do not name the connection they describe.
   */
  async firstActiveConnectionId(): Promise<string | null> {
    const response = await this.apiService.get<{ id: string; status: string }[]>('/erp-connections', { headers: this.authHeaders });
    const connections = Array.isArray(response.data) ? response.data : [];
    return connections.find(c => c.status === 'ACTIVE')?.id ?? null;
  }

  /**
   * Whether the ERP a journal was made on keeps what is sent as a draft; false where sending posts it.
   * Null when the backend did not say, and the pane goes by the company's ERP instead. For a sheet whose
   * manifest does not carry it, which reads the whole journal to learn it.
   */
  async instanceHasDraftState(instanceId: string): Promise<boolean | null> {
    const known = this.instanceDraftStates.get(instanceId);
    if (known !== undefined) return known;
    const response = await this.apiService.get<{ erpHasDraftState?: unknown }>(`/journal/instances/${instanceId}`, { headers: this.authHeaders });
    const flag = response.success ? response.data?.erpHasDraftState : undefined;
    if (typeof flag !== 'boolean') return null;
    this.instanceDraftStates.set(instanceId, flag);
    return flag;
  }

  /** The manifest of a journal sheet, or null for any other sheet. */
  async manifestOf(worksheetId: string): Promise<JournalManifest | null> {
    return this.xmlService.getManifest(worksheetId);
  }

  /** Remove a manifest by worksheet id or worksheet name. */
  async removeManifest(key: string): Promise<void> {
    await this.xmlService.removeManifest(key);
  }

  /**
   * Locate the worksheets a set of manifests point at, in one Excel round trip.
   *
   * Lookup goes by the stable worksheet id first so a renamed sheet is still
   * found; the name is only a fallback for manifests written before ids were
   * recorded. Entries are null where the sheet is genuinely gone.
   */
  private async locateWorksheets(manifests: JournalManifest[]): Promise<(SheetLocation | null)[]> {
    if (manifests.length === 0) return [];

    return Excel.run(async (context) => {
      const probes = manifests.map(manifest =>
        [manifest.worksheetId, manifest.worksheetName]
          .filter((k): k is string => typeof k === 'string' && k.length > 0)
          .map(key => {
            const sheet = context.workbook.worksheets.getItemOrNullObject(key);
            sheet.load(['isNullObject', 'id', 'name']);
            // Values-only so the 500 rows of number formatting laid down by
            // insertTemplate don't read as user data.
            const used = sheet.getUsedRangeOrNullObject(true);
            used.load(['isNullObject', 'rowIndex', 'rowCount']);
            return { sheet, used };
          })
      );
      await context.sync();

      return probes.map((candidates, i) => {
        const hit = candidates.find(c => !c.sheet.isNullObject);
        if (!hit) return null;
        const headerRow = manifests[i]?.headerRow ?? LEGACY_HEADER_ROW;
        const lastRow = hit.used.isNullObject ? headerRow : hit.used.rowIndex + hit.used.rowCount;
        return {
          worksheetId: hit.sheet.id,
          worksheetName: hit.sheet.name,
          headerRow,
          hasData: lastRow > headerRow,
          nextRow: Math.max(lastRow + 1, headerRow + 1),
        };
      });
    });
  }

  /** Resolve a single manifest, healing a drifted worksheet name in place. */
  async resolveSheet(manifest: JournalManifest): Promise<ResolvedJournalSheet | null> {
    const [located] = await this.locateWorksheets([manifest]);
    if (!located) return null;

    const healed = healManifest(manifest, located);
    if (healed !== manifest) await this.xmlService.setManifest(healed);
    return { manifest: healed, ...located };
  }

  /**
   * Every live journal sheet for a template, newest last. Manifests whose sheet
   * has been deleted are pruned so they can't resurrect stale instance ids, and
   * names that drifted through a rename are healed — both in one write.
   */
  async findSheetsForTemplate(templateId: string): Promise<ResolvedJournalSheet[]> {
    const manifests = (await this.xmlService.getAllManifests())
      .filter(m => m.templateId === templateId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const located = await this.locateWorksheets(manifests);

    const sheets: ResolvedJournalSheet[] = [];
    const stale: string[] = [];
    const healedManifests: JournalManifest[] = [];

    manifests.forEach((manifest, i) => {
      const location = located[i];
      if (!location) {
        stale.push(manifest.worksheetId ?? manifest.worksheetName);
        return;
      }
      const healed = healManifest(manifest, location);
      if (healed !== manifest) healedManifests.push(healed);
      sheets.push({ manifest: healed, ...location });
    });

    if (stale.length > 0 || healedManifests.length > 0) {
      await this.xmlService.replaceManifests(stale, healedManifests);
    }
    return sheets;
  }

  /**
   * Persist the journal instance/version onto the sheet's manifest so a saved
   * and reopened workbook keeps pointing at the same ERP draft instead of
   * creating a duplicate on the next validate.
   */
  async updateSheetState(
    manifest: JournalManifest,
    state: Partial<Pick<JournalManifest, 'instanceId' | 'erpHasDraftState' | 'versionId' | 'checkedFingerprint' | 'preparedBy' | 'submittedBy'>>
  ): Promise<JournalManifest> {
    const updated: JournalManifest = { ...manifest, ...state };
    await this.xmlService.setManifest(updated);
    return updated;
  }

  /** Bring a journal sheet to the front and park the cursor on its next free row. */
  async activateSheet(sheet: ResolvedJournalSheet): Promise<void> {
    await Excel.run(async (context) => {
      const worksheet = context.workbook.worksheets.getItem(sheet.worksheetId);
      worksheet.activate();
      worksheet.getRangeByIndexes(sheet.nextRow - 1, 0, 1, 1).select();
      await context.sync();
    });
  }

  /** Id of the worksheet the user is currently looking at. */
  async getActiveWorksheetId(): Promise<string> {
    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.load('id');
      await context.sync();
      return sheet.id;
    });
  }

  /**
   * The template as the Portal offers it now: 'inactive' when the Portal has deactivated it or this
   * company has no such template, null when the backend could not be asked.
   */
  async fetchTemplate(templateId: string): Promise<JournalTemplate | 'inactive' | null> {
    const response = await this.apiService.get<JournalTemplate>(
      `/journal/templates/${templateId}`,
      { headers: this.authHeaders }
    );
    if (response.success && response.data) return response.data.status === 'ACTIVE' ? response.data : 'inactive';
    return response.status === 404 ? 'inactive' : null;
  }

  /**
   * How a sheet's header row compares with the template, which lines are read against by position:
   * the same columns; the template's with new ones only added after them, so the sheet still reads
   * right; or anything else — a column moved, renamed or dropped — where reading would put values in
   * the wrong fields.
   */
  async headerFit(sheet: Pick<ResolvedJournalSheet, 'worksheetId' | 'headerRow'>, template: JournalTemplate): Promise<HeaderFit> {
    const labels = template.columnDefinitions.map(c => c.label.trim());
    return Excel.run(async (context) => {
      const worksheet = context.workbook.worksheets.getItem(sheet.worksheetId);
      const header = worksheet.getRangeByIndexes(sheet.headerRow - 1, 0, 1, labels.length + HEADER_SLACK);
      header.load('values');
      await context.sync();
      return compareHeader((header.values[0] ?? []).map(v => String(v ?? '')), labels);
    });
  }

  /** Whether an empty sheet is still exactly what inserting one from the template would produce. */
  async headerMatches(sheet: ResolvedJournalSheet, template: JournalTemplate): Promise<boolean> {
    return (await this.headerFit(sheet, template)) === 'same';
  }

  /** The company's templates, read with `headers` when the pane is still deciding whose they are. */
  /** Throws when the list could not be read: an empty list would say the company has no templates. */
  async fetchTemplates(headers: Record<string, string> = this.authHeaders): Promise<JournalTemplate[]> {
    const response = await this.apiService.get<JournalTemplate[]>(
      '/journal/templates',
      { headers }
    );
    if (!response.success || !Array.isArray(response.data)) throw refused(response, 'Failed to load journal templates');
    return response.data;
  }

  async insertTemplate(template: JournalTemplate): Promise<InsertedJournalSheet> {
    const owner = this.lists.owner();
    const pair = findAmountPair(template.columnDefinitions);
    const headerRow = pair ? BANDED_HEADER_ROW : LEGACY_HEADER_ROW;
    const headerIndex = headerRow - 1;

    const created = await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load('items/name');
      await context.sync();

      const sheetName = uniqueSheetName(
        `Journal - ${template.displayName}`,
        sheets.items.map(s => s.name)
      );
      const sheet = sheets.add(sheetName);

      const colCount = template.columnDefinitions.length;
      const headers = template.columnDefinitions.map(c => c.label);
      const headerRange = sheet.getRangeByIndexes(headerIndex, 0, 1, colCount);
      headerRange.values = [headers];
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = HEADER_FILL;
      headerRange.format.font.color = '#FFFFFF';

      for (let i = 0; i < colCount; i++) {
        const col = template.columnDefinitions[i];
        if (!col) continue;

        sheet.getRangeByIndexes(headerIndex, i, 1, 1).format.columnWidth = headerColumnWidth(col);

        const dataCol = sheet.getRangeByIndexes(headerRow, i, DATA_ROWS, 1);
        const fmt = this.getNumberFormat(col);
        dataCol.numberFormat = Array.from({ length: DATA_ROWS }, () => [fmt]);
      }

      if (pair) {
        this.writeBalanceBand(sheet, pair, headerRow);
        if (template.showDescription !== false) this.writeDescriptionField(sheet);
      }

      sheet.activate();
      sheet.getRangeByIndexes(headerRow, 0, 1, 1).select();
      sheet.load('id');
      await context.sync();

      return { id: sheet.id, name: sheetName };
    });

    const manifest: JournalManifest = {
      templateId: template.id,
      templateName: template.name,
      worksheetId: created.id,
      worksheetName: created.name,
      headerRow,
      createdAt: new Date().toISOString(),
    };
    await this.xmlService.setManifest(manifest);

    // After the sheet and its manifest exist: a sheet without dropdowns still works, and the check
    // names what is wrong. A failure is reported, though, rather than leave the user hunting for them.
    let listsError: string | undefined;
    try {
      const { expected, failed } = await this.lists.refresh(template, created.id, headerRow, DATA_ROWS, owner);
      if (failed > 0) listsError = `${failed} of ${expected} lists could not be read from the ERP`;
    } catch (error) {
      console.error('Failed to add the journal dropdowns:', error);
      listsError = `Excel said: ${error instanceof Error && error.message ? error.message : 'no reason given'}`;
    }

    return {
      manifest,
      worksheetId: created.id,
      worksheetName: created.name,
      headerRow,
      hasData: false,
      nextRow: headerRow + 1,
      ...(listsError !== undefined ? { listsError } : {}),
    };
  }

  /**
   * Running debit/credit totals above the journal. Plain Excel formulas, so the
   * difference updates as the user types — an out-of-balance journal is visible
   * long before anyone clicks Validate.
   */
  private writeBalanceBand(
    sheet: Excel.Worksheet,
    pair: AmountColumns,
    headerRow: number
  ): void {
    const cells = balanceBandFormulas(pair, headerRow);
    const band = sheet.getRangeByIndexes(0, 0, 1, cells.length);
    band.formulas = [cells];
    band.format.font.bold = true;

    const differenceCell = sheet.getRangeByIndexes(0, 5, 1, 1);
    const balanced = differenceCell.conditionalFormats.add('CellValue');
    balanced.cellValue.format.fill.color = BALANCE_OK_FILL;
    balanced.cellValue.rule = { formula1: '0', operator: 'EqualTo' };

    const off = differenceCell.conditionalFormats.add('CellValue');
    off.cellValue.format.fill.color = BALANCE_OFF_FILL;
    off.cellValue.format.font.color = BALANCE_OFF_FONT;
    off.cellValue.rule = { formula1: '0', operator: 'NotEqualTo' };

    // Keep the totals and headers on screen while scrolling through lines.
    sheet.freezePanes.freezeRows(headerRow);
  }

  /** The label and the text-formatted cell the journal's description goes in. Runs in the caller's batch. */
  private writeDescriptionField(sheet: Excel.Worksheet): void {
    const label = sheet.getRangeByIndexes(DESCRIPTION_ROW_INDEX, 0, 1, 1);
    label.values = [[DESCRIPTION_LABEL]];
    label.format.font.bold = true;
    // Text, so a description such as "2026-11" is not read as a date.
    sheet.getRangeByIndexes(DESCRIPTION_ROW_INDEX, 1, 1, 1).numberFormat = [['@']];
  }

  /**
   * The journal's description as the sheet has it; '' where the sheet has no description field, or
   * its template has the field switched off. Never writes.
   */
  async readJournalDescription(sheet: Pick<ResolvedJournalSheet, 'worksheetId' | 'headerRow'>, template: Pick<JournalTemplate, 'showDescription'>): Promise<string> {
    if (sheet.headerRow !== BANDED_HEADER_ROW || template.showDescription === false) return '';
    return Excel.run(async (context) => {
      const field = context.workbook.worksheets.getItem(sheet.worksheetId).getRangeByIndexes(DESCRIPTION_ROW_INDEX, 0, 1, 2);
      field.load('values');
      await context.sync();
      const [label, value] = (field.values[0] ?? []).map(normalizeDescription);
      return label === DESCRIPTION_LABEL ? value ?? '' : '';
    });
  }

  /**
   * Labels the description field on a banded sheet made before the field existed. Only an empty
   * spacer row is taken over; anything else there is the user's own.
   */
  private async labelDescriptionField(sheet: Pick<ResolvedJournalSheet, 'worksheetId' | 'headerRow'>, template: Pick<JournalTemplate, 'showDescription'>): Promise<void> {
    if (sheet.headerRow !== BANDED_HEADER_ROW || template.showDescription === false) return;
    await Excel.run(async (context) => {
      const worksheet = context.workbook.worksheets.getItem(sheet.worksheetId);
      const field = worksheet.getRangeByIndexes(DESCRIPTION_ROW_INDEX, 0, 1, 2);
      field.load('values');
      await context.sync();
      if ((field.values[0] ?? []).every(v => normalizeDescription(v) === '')) {
        this.writeDescriptionField(worksheet);
        await context.sync();
      }
    });
  }

  /**
   * After an edit: reduces dropdown picks to their codes and marks values their list does not have,
   * and with `repoint` points edited accounts at the lists of their rows' types. An edit across a
   * multiple selection arrives as one address per area, comma-separated. A cell is noted once, though
   * an account is looked at beside a changed type as well as in its own area.
   */
  async markListValues(worksheetId: string, address: string, template: JournalTemplate, headerRow: number, repoint = true): Promise<ListValueNote[]> {
    const areas = address.split(',').map(a => a.trim()).filter(a => a !== '');
    const notes = await Promise.all(areas.map(area => this.lists.onCellsChanged(worksheetId, area, template, headerRow, DATA_ROWS, repoint)));
    const seen = new Set<string>();
    return notes.reduce<ListValueNote[]>((all, some) => all.concat(some), []).filter(note => {
      const cell = `${note.lineNumber}|${note.column}`;
      if (seen.has(cell)) return false;
      seen.add(cell);
      return true;
    });
  }

  /**
   * After a check: the sheet's dropdowns re-read from the ERP, so they offer what it has now, and a
   * missing description label added. Best effort, both — a protected sheet keeps what it has.
   */
  async refreshLists(
    template: JournalTemplate, sheet: Pick<ResolvedJournalSheet, 'worksheetId' | 'headerRow'>, owner: ListOwner = this.lists.owner()
  ): Promise<void> {
    await Promise.all([
      this.lists.refresh(template, sheet.worksheetId, sheet.headerRow, DATA_ROWS, owner)
        .catch((error: unknown) => console.error('Failed to refresh the journal dropdowns:', error)),
      this.labelDescriptionField(sheet, template)
        .catch((error: unknown) => console.error('Failed to label the journal description:', error)),
    ]);
  }

  /**
   * "Refresh dropdowns": the sheet's lists read again from the ERP and its dropdowns pointed at them.
   * Refused for a sheet whose columns no longer line up with the template, where dropdowns would land
   * in the wrong columns. Throws when Excel would not take the dropdowns.
   */
  async refreshListsFromErp(template: JournalTemplate, sheet: Pick<ResolvedJournalSheet, 'worksheetId' | 'headerRow'>): Promise<'misfit' | ListRefresh> {
    if (await this.headerFit(sheet, template) === 'different') return 'misfit';
    return this.lists.refresh(template, sheet.worksheetId, sheet.headerRow, DATA_ROWS, this.lists.owner(), true);
  }

  /**
   * Every coded cell of the sheet looked at against its list, so a value the ERP has gained loses its
   * mark. Follows a refresh, which has just pointed every dropdown, so it points none.
   */
  async remarkSheet(template: JournalTemplate, sheet: Pick<ResolvedJournalSheet, 'worksheetId' | 'headerRow'>): Promise<ListValueNote[]> {
    // One read of the list sheet, rather than one per column.
    await this.lists.itemsOfScope();
    return this.markListValues(sheet.worksheetId, this.listColumnsAddress(template, sheet.headerRow), template, sheet.headerRow, false);
  }

  /** Whose lists an edit is marked against, for a re-check started from it to keep. */
  listOwner(): ListOwner {
    return this.lists.owner();
  }

  /**
   * After an edit left values their lists lack: those lists read again from the ERP, unless they
   * were moments ago, and pointed at by the sheet. Answers whether any list was written.
   */
  async refreshListsFor(
    template: JournalTemplate, sheet: { worksheetId: string; headerRow: number }, notes: ListValueNote[], owner: ListOwner
  ): Promise<boolean> {
    if (notes.length === 0) return false;
    return this.lists.refreshListsOf(template, new Set(notes.map(note => note.list)), { ...sheet, rows: DATA_ROWS }, owner);
  }

  /**
   * The edited cells looked at again against the lists as the workbook holds them now, and which of
   * the values asked about are in those lists now: found in the ERP, whatever the cell holds since.
   * The edit pointed its own dropdowns, and a list read again points the sheet, so this points none.
   */
  async recheckMarks(
    worksheetId: string, address: string, template: JournalTemplate, headerRow: number, asked: ListValueNote[]
  ): Promise<{ remaining: ListValueNote[]; found: ListValueNote[] }> {
    const remaining = await this.markListValues(worksheetId, address, template, headerRow, false);
    const items = await this.lists.itemsOfScope();
    const found = asked.filter(note => {
      const col = template.columnDefinitions.find(c => c.name === note.column);
      const list = items.get(note.list);
      return col !== undefined && list !== undefined
        && Array.from(list).some(item => matchFormOf(col, codeOfListItem(item)) === matchFormOf(col, note.value));
    });
    return { remaining, found };
  }

  /**
   * The data cells of every coded column, one area per column, as an edit event would name them. An
   * account whose type column has a list is left to that column's area, which reads it with the types.
   */
  private listColumnsAddress(template: JournalTemplate, headerRow: number): string {
    const first = headerRow + 1;
    const last = headerRow + DATA_ROWS;
    const columns = template.columnDefinitions;
    const besideType = new Set(accountPairsOf(template)
      .filter(pair => {
        const type = pair.type === null ? undefined : columns[pair.type];
        return type !== undefined && listKeyOf(type) !== null;
      })
      .map(pair => pair.account));
    return columns
      .map((col, index) => (listKeyOf(col) && !besideType.has(index) ? `${columnLetter(index)}${first}:${columnLetter(index)}${last}` : ''))
      .filter(area => area !== '')
      .join(',');
  }

  /** `worksheetKey` is a worksheet id where available, falling back to a name. */
  async readJournalLines(
    template: JournalTemplate,
    worksheetKey: string,
    headerRow = LEGACY_HEADER_ROW
  ): Promise<JournalLine[]> {
    const columns = template.columnDefinitions;
    const pairs = accountPairsOf(template);
    const listItems = await this.lists.itemsOfScope().catch(() => new Map<string, Set<string>>());

    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(worksheetKey);
      const used = sheet.getUsedRangeOrNullObject(true);
      used.load(['isNullObject', 'rowIndex', 'rowCount']);
      await context.sync();

      if (used.isNullObject) return [];
      const lastRow = used.rowIndex + used.rowCount;
      if (lastRow <= headerRow) return [];

      // Read the data block by address rather than the used range, so a
      // balance band above and anything the user parks to the right are both
      // outside what counts as a journal line.
      const block = sheet.getRangeByIndexes(headerRow, 0, lastRow - headerRow, columns.length);
      block.load('values');
      await context.sync();

      const lines: JournalLine[] = [];
      for (const row of block.values as (string | number | boolean)[][]) {
        if (!row || row.every(cell => cell === null || cell === '' || cell === undefined)) continue;

        const line: JournalLine = {};
        for (let c = 0; c < columns.length; c++) {
          const col = columns[c];
          if (!col) continue;
          // An account is a pick from the list its row's type names.
          const pair = pairs.find(p => p.account === c);
          const key = pair ? accountListOf(pair, pair.type === null ? undefined : row[pair.type]) : listKeyOf(col);
          line[col.name] = readCellValue(row[c], col, key ? listItems.get(key) : undefined);
        }
        lines.push(line);
      }

      return lines;
    });
  }

  async validate(_instanceId: string, versionId: string): Promise<JournalValidationResult> {
    const response = await this.apiService.post<JournalValidationResult>(
      `/journal/versions/${versionId}/validate`,
      {},
      { headers: this.authHeaders }
    );
    if (!response.success || !response.data) throw refused(response, 'Validation failed');
    return response.data;
  }

  /**
   * Throws the backend's refusal, so the pane can say why. Sends no currency: the ERP posts each line
   * in its own currency or the company's, so one set on the journal would only be shown, and wrong.
   * Answers whether the journal's ERP keeps what is sent as a draft, where the backend says.
   */
  async createInstance(templateId: string, erpConnectionId: string, name: string, journalDate: string): Promise<{ id: string; erpHasDraftState?: boolean }> {
    const response = await this.apiService.post<{ id: string; erpHasDraftState?: boolean }>(
      '/journal/instances',
      { templateId, erpConnectionId, name, journalDate },
      { headers: this.authHeaders }
    );
    if (!response.success || !response.data?.id) throw refused(response, 'Failed to create journal instance');
    return response.data;
  }

  /**
   * `journalName` renames the journal, so a changed description follows it into the ERP. Throws the
   * backend's refusal, so the pane can say why.
   */
  /** The version the backend kept, with who made it: the version submitted, where an approver checks the same lines. */
  async createVersion(instanceId: string, lines: JournalLine[], journalName?: string): Promise<{ id: string; createdById?: string }> {
    const response = await this.apiService.post<{ id: string; createdById?: string }>(
      `/journal/instances/${instanceId}/versions`,
      journalName ? { lines, journalName } : { lines },
      { headers: this.authHeaders }
    );
    if (!response.success || !response.data?.id) throw refused(response, 'Failed to create journal version');
    return response.data;
  }

  /**
   * Hand the journal to someone with push rights. The backend re-checks that
   * the latest version validated cleanly, so a stale button is refused rather
   * than queueing a broken journal.
   */
  async submitForApproval(instanceId: string): Promise<{ submittedById?: string }> {
    const response = await this.apiService.post<{ submittedById?: string }>(
      `/journal/instances/${instanceId}/submit`,
      {},
      { headers: this.authHeaders }
    );
    if (!response.success) throw refused(response, 'Send for review failed');
    return response.data ?? {};
  }

  async pushDraft(versionId: string): Promise<JournalJob> {
    const response = await this.apiService.post<JournalJob>(
      `/journal/versions/${versionId}/push`,
      {},
      { headers: this.authHeaders }
    );
    if (!response.success || !response.data) throw refused(response, 'Push failed');
    return response.data;
  }

  async postJournal(versionId: string): Promise<JournalJob> {
    const response = await this.apiService.post<JournalJob>(
      `/journal/versions/${versionId}/post`,
      {},
      { headers: this.authHeaders }
    );
    if (!response.success || !response.data) throw refused(response, 'Post failed');
    return response.data;
  }

  /**
   * "Finalize & post" in one click: posts the version's draft, sending it to the ERP first when it
   * has none yet. Stops after the send when the send was refused or the ERP approves and posts the
   * journal itself. Nothing is sent twice: each step is one request.
   */
  async finalizeAndPost(versionId: string): Promise<{ job: JournalJob; sentDraftOnly: boolean }> {
    try {
      return { job: await this.postJournal(versionId), sentDraftOnly: false };
    } catch (error) {
      if ((error as { code?: string } | null)?.code !== 'NO_DRAFT') throw error;
    }
    const pushed = await this.pushDraft(versionId);
    if (pushed.status === 'FAILED' || pushed.status === 'VALIDATION_ERRORS' || awaitsApprovalInErp(pushed)) {
      return { job: pushed, sentDraftOnly: true };
    }
    return { job: await this.postJournal(versionId), sentDraftOnly: false };
  }

  async getJobStatus(jobId: string): Promise<JournalJob | null> {
    const response = await this.apiService.get<JournalJob>(
      `/journal/jobs/${jobId}`,
      { headers: this.authHeaders }
    );
    return response.data ?? null;
  }

  async renderErrors(
    errors: ValidationError[],
    template: JournalTemplate,
    worksheetKey: string,
    headerRow = LEGACY_HEADER_ROW
  ): Promise<void> {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(worksheetKey);

      for (const err of errors) {
        // lineNumber 0 marks a journal-level problem such as an unbalanced
        // total; it belongs to no cell and is reported in the task pane.
        if (err.lineNumber === 0) continue;
        const colIndex = template.columnDefinitions.findIndex(c => c.name === err.column);
        if (colIndex < 0) continue;

        const cell = sheet.getRangeByIndexes(headerRow - 1 + err.lineNumber, colIndex, 1, 1);
        cell.format.fill.color = err.severity === 'error' ? ERROR_FILL : WARNING_FILL;
        const comment = sheet.comments.add(cell, err.message);
        comment.load();
      }
      await context.sync();
    });
  }

  async clearErrors(
    template: JournalTemplate,
    worksheetKey: string,
    headerRow = LEGACY_HEADER_ROW
  ): Promise<void> {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(worksheetKey);
      const used = sheet.getUsedRangeOrNullObject(true);
      used.load(['isNullObject', 'rowIndex', 'rowCount']);
      // Comments from an earlier round would otherwise stack up, and Excel
      // rejects a second comment on a cell that already has one.
      const comments = sheet.comments;
      comments.load('items');
      await context.sync();

      for (const comment of comments.items) comment.delete();

      // Clear the data block only, so the balance band's conditional
      // formatting above the header survives.
      const lastRow = used.isNullObject ? headerRow : used.rowIndex + used.rowCount;
      if (lastRow > headerRow) {
        sheet
          .getRangeByIndexes(headerRow, 0, lastRow - headerRow, template.columnDefinitions.length)
          .format.fill.clear();
      }

      const headerRange = sheet.getRangeByIndexes(headerRow - 1, 0, 1, template.columnDefinitions.length);
      headerRange.format.fill.color = HEADER_FILL;

      await context.sync();
    });
  }

  private getNumberFormat(col: JournalColumnDefinition): string {
    switch (col.type) {
      case 'date': return 'yyyy-mm-dd';
      case 'number': return '#,##0';
      case 'decimal': return '#,##0.00';
      default: return '@'; // Text — prevents Excel from auto-interpreting values
    }
  }
}
